import {Box, Center, createStyles, Loader} from '@mantine/core';
import React from 'react';

import {OligoAwareFolderItemType, TagSchema} from 'constants/RegistryConstants';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {SchemasWithInterfacesQuery} from 'schemas/schema-interface/__generated/SchemasWithInterfacesQuery';
import {SCHEMAS_WITH_INTERFACES_QUERY} from 'schemas/schema-interface/SchemaInterfaceConstants';
import QueryingTagSchemaSelector from 'schemas/shared/TagSchemaSelector';

// The schema selector's trigger button sizes to its label, which leaves it narrow for short schema
// names. Stretch it to fill the editor row (the selector exposes no width prop of its own).
const useStyles = createStyles({
  fullWidth: {
    width: '100%',
    '& .selectDropdown-button': {
      width: '100%',
    },
  },
});

interface SchemaEditorProps {
  value: string | null;
  onChange: (schemaId: string | null) => void;
  required: boolean;
  disabled?: boolean;
  // Restricts the picker to schemas of these entity types; null (the default) shows all schemas.
  oligoAwareFolderItemTypes?: Array<OligoAwareFolderItemType> | null;
  // Narrows the picker to schemas implementing this system schema interface (e.g. `ssi_dnadomain`).
  implementsSchemaInterfaceId?: string;
}

// An interface filter is either still loading its schema ids or ready with the (possibly empty) set that
// narrows the picker.
type SchemaAllowlist = {status: 'loading'} | {status: 'ready'; allowedIds: Set<string>};

/**
 * Resolves the set of schema ids implementing the requested interface. Returns `null` when no interface
 * is requested, in which case the picker shows every schema.
 */
function useSchemasImplementingInterface(
  implementsSchemaInterfaceId: string | undefined
): SchemaAllowlist | null {
  const isInterfaceFilter = implementsSchemaInterfaceId != null;

  const interfaceQuery = useSkippableBenchlingQuery<SchemasWithInterfacesQuery>(
    SCHEMAS_WITH_INTERFACES_QUERY,
    {
      skip: !isInterfaceFilter,
    }
  );

  if (!isInterfaceFilter) {
    return null;
  }
  if (interfaceQuery.type === QueryResultType.PENDING) {
    return {status: 'loading'};
  }
  // A failed (or otherwise non-success) query settles into a ready, empty allowlist rather than
  // loading forever, so the picker becomes usable instead of hanging on a permanent spinner.
  if (interfaceQuery.type !== QueryResultType.SUCCESS) {
    return {status: 'ready', allowedIds: new Set()};
  }
  return {
    status: 'ready',
    allowedIds: new Set(
      interfaceQuery.data.tagSchemas
        .filter((schema) => schema.interfaces.some((iface) => iface.id === implementsSchemaInterfaceId))
        .map((schema) => schema.id)
    ),
  };
}

export default function SchemaEditor({
  value,
  onChange,
  required,
  disabled,
  oligoAwareFolderItemTypes = null,
  implementsSchemaInterfaceId,
}: SchemaEditorProps): JSX.Element {
  const {classes} = useStyles();
  const allowlist = useSchemasImplementingInterface(implementsSchemaInterfaceId);

  // While the interface allowlist is still resolving, render a loading state directly rather than the
  // picker: the underlying selector's own loading indicator keys off its tag-schema query, which may
  // already be cached, so it would otherwise show an enabled, empty-filtered dropdown during this window.
  if (allowlist?.status === 'loading') {
    return (
      <Center className={classes.fullWidth}>
        <Loader aria-label="Loading schemas" />
      </Center>
    );
  }

  const tagSchemaFilterFn =
    allowlist == null ? undefined : (schema: TagSchema): boolean => allowlist.allowedIds.has(schema.id);
  return (
    <Box className={classes.fullWidth}>
      <QueryingTagSchemaSelector
        // Empty string is unset per the config contract; the selector expects a real id or null.
        selectedTagSchemaId={value || null}
        allowNone={!required}
        isDisabled={Boolean(disabled)}
        oligoAwareFolderItemTypes={oligoAwareFolderItemTypes}
        tagSchemaFilterFn={tagSchemaFilterFn}
        shouldDefaultIfSingleItem={!disabled}
        onChange={(schema: TagSchema | null) => {
          onChange(schema?.id ?? null);
        }}
        dataTestAttrs={{component: 'SchemaEditor', element: 'selectDropdown'}}
      />
    </Box>
  );
}
